<template>
  <div>
  <modal
    ref="modal"
    name="module-access-overlay"
    :adaptive="true"
    :scrollable="true"
    :reset="true"
    width="500px"
    height="auto"
    :max-width="800"
    :click-to-close="false"
    :esc-to-close="false"
    :classes="['v--modal-box', 'v--modal']"
    :pivot-y="0.1"
    @before-open="beforeOpen"
    @closed="onModalClosed"
  >
    <div class="bg-white p-8 text-center max-w-[500px] w-full mx-auto my-5">

      <h2 class="text-2xl font-bold mb-4">{{ title(mainPlan) }}</h2>
      <p class="mb-6 text-gray-700 text-sm">
        {{ description(mainPlan) }}
      </p>
      <!-- PLANS SECTION -->
      <div v-if="mainPlan" class="mb-5 flex flex-col flex-wrap justify-center items-stretch gap-4 ">
        <div class="flex-1 flex flex-row justify-between items-center p-6 rounded-lg border shadow-md">
          <div v-if="mainPlan.images?.length" class="flex-col justify-center mr-3">
            <img :src="mainPlan.images[0]" :alt="mainPlan.name + ' image'" class="h-16 object-contain" />
          </div>
          <div class="flex-col text-left flex-1">
            <div class="font-bold text-lg mb-1 flex w-full">{{ mainPlan.name }}</div>
            <div class="text-gray-600 text-sm mb-2">{{ mainPlan.description }}</div>
          </div>
          <div class="font-bold text-xl text-center mb-2 pricing-section">
             {{ formatCurrencyCents(mainPlan.amount , mainPlan.currency) }}
            <span class="font-medium text-sm" v-if="mainPlan.recurring">/ {{ mainPlan.recurring }}</span>
          </div>
        </div>
        <div class="my-3">
          <button @click="gotToCheckout(mainPlan)"
            class="w-full text-center bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md">
            Upgrade Plan
          </button>
        </div>
      </div>
      <!-- ADDONS SECTION -->
      <div v-if="hasAddons || showAddonLimitReached">
        <div class="flex flex-col w-full p-3" v-if="!isSubscriptionPastDue">
          <div v-if="showAddonLimitReached" class="text-center mb-4">
            <p class="text-red-600 font-bold">{{ addonLimitMessage }}</p>
          </div>
          <label v-for="addon in addons" :key="addon.id"
            class="flex flex-col items-center bg-white w-full">
            <div class="flex flex-row items-center p-3 rounded-lg border shadow-sm w-full">
            <div v-if="addon.images && addon.images.length" class="flex-shrink-0 mr-3">
              <img :src="addon.images[0]" :alt="addon.name + ' image'" class="h-8 w-8 object-contain" />
            </div>
            <div class="flex-1 min-w-0 text-left">
              <div class="font-medium text-base truncate">{{ addon.name }}</div>
              <div class="text-gray-500 text-xs whitespace-normal leading-snug mt-0.5">{{ addon.description }}</div>
            </div>
            <div class="flex items-center ml-4 space-x-2">
              <input type="number" :min="getBlockSize(addon)" :step="getBlockSize(addon)"
                v-model.number="addonQuantities[addon.id]" @input="updateAddonQuantity($event, addon)"
                class="w-20 border rounded px-1 py-1 text-sm text-center" :placeholder="getBlockSize(addon)"
                title="Enter quantity in multiples of block size" />
              <span class="text-xs text-gray-600">x {{ formatCurrencyCents(addon.amount, addon.currency) }}</span>
              <span class="text-xs text-blue-700 font-semibold">= {{ formatCurrencyCents(totalAmount(addon), addon.currency) }}</span>
              <span v-if="addon.recurring" class="text-xs text-gray-400 ml-1">/ {{ addon.recurring }}</span>
            </div>
          </div>
          <button 
            type="button"
            @click="showAddonConfirmation({ ...addon, quantity: addonQuantities[addon.id] || getBlockSize(addon) })"
            class="my-3  w-full flex flex-row items-center justify-center text-center bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md"
            :disabled="!addonQuantities[addon.id] || addonQuantities[addon.id] < getBlockSize(addon)">
            <inline-svg name="add" classes="w-4 h-4 text-white fill-current mr-1" />
            <span>Add {{ addon.name }}</span>
          </button>
    
          </label>
        </div>
        <div class="flex flex-col w-full px-3">
          <div v-if="isSubscriptionPastDue" class="bg-yellow-50 border-yellow-400 p-4 mb-6 rounded">
            <div class="flex flex-col">
              <div class="flex items-start">
                <div class="flex-1">
                  <p class="text-sm text-yellow-700 mb-3" v-html="pastDueMessage">
                  </p>
                  <button 
                    @click="payOutstandingInvoice"
                    class="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                  >
                    <svg class="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.616 1.14 2.853 1.14 1.237 0 2.29-.491 2.853-1.14a1 1 0 00-1.51-1.31c-.163.187-.452.377-.843.504v-1.94c.391-.127.68-.317.843-.504a1 1 0 001.51 1.31c.562-.648 1.324-1.14 2.324-1.14 1.237 0 2.29.491 2.853 1.14a1 1 0 10-1.51 1.31c-.163-.187-.452-.377-.843-.504v1.941c.391-.127.68-.317.843-.504a1 1 0 10-1.51-1.31c-.163.187-.452.377-.843.504v-1.94c.391-.127.68-.317.843-.504a1 1 0 10-1.51-1.31c-.163.187-.452.377-.843.504v-1.94z" clip-rule="evenodd" />
                    </svg>
                    Pay Now
                  </button>
                </div>
              </div>
            </div>
          </div>
          <button type="button" v-if="moduleData?.has_access"
              @click="close()"
              class="mb-3  w-full flex flex-row items-center justify-center text-center bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-md">
              <span>Cancel</span>
            </button>
            </div>
        
      </div>
    </div>
  </modal>
    <!-- Add the AddonConfirmation component -->
    <addon-confirmation ref="addonConfirmation" />
    </div>
</template>

<script>
import { formatCurrencyCents } from '../utils/formatters';
import { mapGetters } from 'vuex';
import AddonConfirmation from './AddonConfirmation.vue';
export default {
  components: {
    AddonConfirmation
  },
  data() {
    return {
      loading: false,
      error: null,
      moduleData: null,
      moduleSlug: null,
      currentModuleName: this.moduleName || window.moduleName || '', // Local data property
      localSuggestedPlans: [],
      showAddonLimitReached: false,
      addonLimitMessage: '',
      targetModal: null,
      addonQuantities: {},
      isAddingAddon: false,
    };
  },
  props: {
    moduleName: { 
      type: String, 
      required: false, 
      default: () => window.moduleName 
    },
    userId: { type: Number, required: false, default: window.user?.id ?? null },
    clientId: { type: Number, required: false, default: window.user?.client_id ?? null },
    // To control requirement, provide as prop: "requiredType": "plan"|"addon"|"both"
    requiredType: { type: String, default: "plan" },
    // Auto show the modal when component is mounted
    autoShow: { type: Boolean, default: true }
  },
  computed: {
    ...mapGetters('moduleAccess', ['allModulesAccessData']),
    // Check if subscription is past due
    isSubscriptionPastDue() {
      return this.moduleData?.subscription_details?.status === 'past_due';
    },
    // Get past due message
    pastDueMessage() {
      let endDate = '';
      const periodEnd = this.moduleData?.subscription_details?.period_end;
      if (periodEnd) {
        try {
          // Handle both timestamp (number) and ISO string formats
          const date = new Date(periodEnd);
          if (!isNaN(date.getTime())) {
            endDate = date.toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            });
          }
        } catch (e) {
          console.error('Error parsing date:', e);
        }
      }
      
      return `Outstanding invoice - action needed. Please pay before ${endDate ? `<span class="font-semibold">${endDate}</span>` : 'the end of the billing period'} to avoid service interruption.`;
    },
    // Get module by slug from Vuex store
    moduleAccessStore() {
      return this.$store.getters['moduleAccess/getModuleBySlug'];
    },
    
    // Get suggested plans from module data
    suggestedPlans() {
      if (typeof window === 'undefined') return [];
      const moduleData = this.findModuleBySlug(this.moduleName || window.moduleName);
      return moduleData?.suggested_plans || [];
    },
    
    mainPlan() {
      if (!this.localSuggestedPlans.length) return null;
      return this.localSuggestedPlans.find(p => p.is_bundle && p.category === 'plan')
        || this.localSuggestedPlans.find(p => p.category === 'plan')
        || null;
    },
    
    addons() {
      return this.localSuggestedPlans.filter(plan => plan.category === 'addon');
    },
    
    hasAddons() {
      return this.addons.length > 0;
    },
  },
  mounted() {
    // Set up event listeners for global events
    this.$moduleAccessOverlay.$on('show-module-access-overlay', this.handleShowEvent);
    this.$moduleAccessOverlay.$on('hide-module-access-overlay', this.close);
    if (this.autoShow && this.moduleName) {
      this.$nextTick(() => {
        const moduleData = this.findModuleBySlug(this.moduleName);
        if (moduleData?.has_access === false) {
          this.open(this.moduleName);
        }
      });
    }
    
    // Listen for popup opened events
    this.$root.$on('popup:opened', this.handlePopupOpened);
  },
  beforeDestroy() {
    // Clean up event listeners
    this.$root.$off('popup:opened', this.handlePopupOpened);
    this.$moduleAccessOverlay.$off('show-module-access-overlay', this.handleShowEvent);
    this.$moduleAccessOverlay.$off('hide-module-access-overlay', this.close);
  },

  watch: {
    addons: {
    handler(newAddons) {
      if (Array.isArray(newAddons) && newAddons.length) {
        newAddons.forEach(addon => {
          // Try to get quantity from subscription_details or addon.subscription_quantity
          let quantity = null;
          // Example: If you store all existing addon subscriptions in moduleData.subscription_details.addons
          // and match by addon.id:
          if (
            this.moduleData &&
            this.moduleData.subscription_details &&
            Array.isArray(this.moduleData.subscription_details.addons)
          ) {
            const existing = this.moduleData.subscription_details.addons.find(
              x => x.addon_id === addon.id
            );
            if (existing) {
              quantity = existing.quantity;
            }
          }
          // Or, if addon has subscription_quantity property:
          if (addon.subscription_quantity != null) {
            quantity = addon.subscription_quantity;
          }

          // Fallback to block size if nothing is set
          if (!quantity) {
            quantity = this.getBlockSize(addon);
          }
          console.log('quantity', quantity);
          console.log('addon', addon);
          console.log('this.addonQuantities', this.addonQuantities);

          //if (!this.addonQuantities[addon.id])
           {
            this.$set(this.addonQuantities, addon.id, quantity);
          }
        });
      }
    },
    immediate: true,
    deep: true,
  },
    // Update local module name when prop changes
    moduleName: {
      handler(newVal) {
        this.$nextTick(() => {
          console.log('moduleName prop changed', newVal);
          this.currentModuleName = newVal || window.moduleName || '';
        });
      },
      immediate: true
    },
    // Update local suggested plans when prop changes
    suggestedPlans: {
      handler(newVal) {
        this.localSuggestedPlans = Array.isArray(newVal) ? [...newVal] : [];
      },
      immediate: true,
      deep: true
    },
    // Watch for changes to window.moduleName
    '$store.state.moduleName': {
      handler(newVal) {
        if (newVal) {
          this.currentModuleName = newVal;
          this.$nextTick(() => {
            if (this.showModal) {
              this.beforeOpen({ params: { moduleSlug: newVal } });
            }
          });
        }
      },
      immediate: true
    }
  },

  methods: {
    formatCurrencyCents,
    // Public method to open the modal
    async open(moduleSlug, options = {}) {
      if (moduleSlug) {
        this.moduleSlug = moduleSlug;
        this.localModuleName = options.moduleName || moduleSlug;
        this.targetModal = options.targetModal || null;
        
        // Force update the module name in window for legacy code
        if (options.forceUpdate) {
          window.moduleName = this.localModuleName;
        }
        
        await this.$store.dispatch('moduleAccess/fetchModulesAccessData');
        // Initialize module data
        this.initModuleData(moduleSlug);
        
        // Check if we can bypass the overlay and open target modal directly
        if (this.targetModal && this.moduleData) {
          if (!this.shouldShowOverlay()) {
            console.log('Bypassing overlay, opening target modal directly:', this.targetModal);
            this.$nextTick(() => {
              this.$modal.hide('module-access-overlay');
              setTimeout(() => {
                this.$modal.show(this.targetModal);
              }, 0);
            });
            return;
          }
        }
      }
      
      if (this.$refs.modal) {
        this.$refs.modal.visible = true;
      } else {
        this.$modal.show('module-access-overlay', { moduleSlug, ...options });
      }
    },
    
    // Public method to close the modal
    close() {
      if (this.$refs.modal) {
        this.$refs.modal.visible = false;
      } else {
        this.$modal.hide('module-access-overlay');
      }
    },
    handleShowEvent({ moduleSlug, ...options }) {
      console.log('handleShowEvent called with:', { moduleSlug, options });
      this.moduleSlug = moduleSlug;
      this.currentModuleName = options.moduleName || moduleSlug;
      if (options.forceUpdate) {
        window.moduleName = this.currentModuleName;
      }
      Object.assign(this, options);
      this.open(moduleSlug, options);
    },
    checkAddonLimits(moduleData) {
      if (moduleData?.subscription_details?.category === 'addon') {
        const { quantity: allowed, used_quantity: used } = moduleData.subscription_details;
        const remaining = Math.max(0, allowed - used);
        
        // Initialize addon quantities with the block size or remaining needed
        if (this.addons && this.addons.length) {
          this.addons.forEach(addon => {
            const blockSize = this.getBlockSize(addon);
            const suggestedQty = Math.max(blockSize, Math.ceil(remaining / blockSize) * blockSize);
            this.$set(this.addonQuantities, addon.id, suggestedQty);
          });
        }
        
        if (used >= allowed) {
          this.showAddonLimitReached = true;
          this.addonLimitMessage = `You have reached the limit of ${allowed} ${moduleData.module?.name || 'items'} in your current plan.`;
          return true;
        }
      }
      this.showAddonLimitReached = false;
      return false;
    },
    /**
     * Find module data by slug from the Vuex store
     * @param {string} slug - The module slug to find
     * @returns {Object|null} The module data or null if not found
     */
    findModuleBySlug(slug) {
      if (!slug) return null;
      return this.$store.getters['moduleAccess/getModuleBySlug'](slug);
    },
    
    /**
     * Initialize module data from slug
     * @param {string} slug - The module slug to initialize
     */
    initModuleData(slug) {
      this.moduleSlug = slug;
      window.moduleName = slug;
      this.localModuleName = slug;
      
      // Find the module data from the global window object
      this.moduleData = this.findModuleBySlug(slug);
      
      if (!this.moduleData) {
        this.error = 'Module data not found';
        return false;
      }
      
      // Initialize suggested plans if available
      if (this.moduleData.suggested_plans) {
        this.localSuggestedPlans = [...this.moduleData.suggested_plans];
      }
      
      return true;
    },
    
    handlePopupOpened(eventData) {
      // Reset state when popup is opened
      this.loading = false;
      this.error = null;
      this.showAddonLimitReached = false;
      
      if (eventData?.moduleSlug) {
        this.initModuleData(eventData.moduleSlug);
        
        // Check addon limits
        this.handleAddonLimits();
      }
    },
    show() {
      this.$modal.show('module-access-overlay');
    },
    onModalClosed() {
      // Clean up any state when modal is closed
      this.showAddonLimitReached = false;
      this.addonLimitMessage = '';
      this.targetModal = null;
      // Emit closed event if parent components need to know
      this.$emit('closed');
    },
    showAddonConfirmation(plan) {
      if (!this.moduleData?.has_access) {
        this.addAddon(plan);
        return;
      }
      
      // Get the current user's data from Vuex store or window object
      const userData = this.$store.state.auth?.user || window.user || {};
      
      const existingQuantity = parseInt(this.moduleData?.subscription_details?.quantity || 0, 10);
      const newQuantity = parseInt(this.addonQuantities[plan.id] || plan?.block_size || 0, 10);
      const totalQuantity = this.getTotalAddonQuantity(plan);
      
      const confirmationData = {
        ...plan,
        existingQuantity,
        newQuantity,
        totalQuantity,
        currentPrice: plan.amount * newQuantity,
        isUpdate: this.showAddonLimitReached || false,
        subscriptionId: this.moduleData?.subscription_details?.stripe_subscription_id || null,
        customerId: userData.stripe_id || null,
        // Ensure we have the raw amount (in cents) for calculations
        amount: plan.amount || plan.price_amount || 0,
        // Format the amount for display
        formattedAmount: plan.formatted_amount || (plan.amount ? formatCurrencyCents(plan.amount, plan.currency) : '$0.00'),
        // Add recurring info if available
        recurring: plan.interval || 'month',
        // Add the block size for reference
        blockSize: plan.block_size || 1
      };
      
      this.$refs.addonConfirmation.show(confirmationData, this.addAddon);
    },
    async beforeOpen(event, params) {
      // Reset state
      this.loading = true;
      this.error = null;
      this.showAddonLimitReached = false;
      
      try {
        // Always refresh module access data when the modal opens
        await this.$store.dispatch('moduleAccess/fetchModulesAccessData');
      } catch (error) {
        console.error('Failed to refresh module access data:', error);
        // Don't block the modal from opening if refresh fails
      }
      
      if (event?.params?.moduleSlug) {
        // Store params in local variables to maintain reference
        const moduleSlug = event.params.moduleSlug;
        const targetModal = event.params.targetModal || null;
        
        // Find the module data from the Vuex store
        const moduleData = this.$store.getters['moduleAccess/getModuleBySlug'](moduleSlug);
        
        if (!moduleData) {
          console.log('No module data found for:', moduleSlug);
          this.error = 'Module data not found';
          this.loading = false;
          return;
        }
        
        // Update component state
        this.moduleSlug = moduleSlug;
        this.targetModal = targetModal;
        this.moduleData = moduleData;
        
        // Initialize suggested plans
        if (moduleData.suggested_plans?.length) {
          this.localSuggestedPlans = [...moduleData.suggested_plans];
        }
        
        this.loading = false;
        
        // Check if we need to show the overlay or can proceed directly
        if (this.shouldShowOverlay()) {
          console.log('Showing module access overlay - access check failed');
          // The overlay will be shown with the appropriate error message
          return;
        }
        
        if (!targetModal) {
          console.error('No target modal specified');
          return;
        }
        
        // Store the target modal in a local variable to prevent reactivity issues
        const modalToOpen = targetModal;
        
        // Close this modal first
        this.$nextTick(() => {
          this.$modal.hide('module-access-overlay');
          
          // Small delay to ensure modal is fully closed before opening the next one
          setTimeout(() => {
            this.$modal.show(modalToOpen);
          }, 0);
        });
      } else {
        this.error = 'Module slug not provided';
        this.loading = false;
      }
    },
    shouldShowOverlay() {
      if (!this.moduleData) {
        console.log('No module data found');
        // If no module data, show error
        this.showAddonLimitReached = false;
        this.addonLimitMessage = 'Module information could not be loaded. Please try again.';
        return true;
      }
      
      // Check if subscription is past due
      if (this.isSubscriptionPastDue) {
        this.showAddonLimitReached = true;
        this.addonLimitMessage = this.pastDueMessage;
        return true;
      }
      
      // Check if user has access to this module
      if (this.moduleData?.has_access === false) {
        console.log('No access to this module');
        this.showAddonLimitReached = false;
        this.addonLimitMessage = 'You do not have access to this module. Please upgrade your plan.';
        return true;
      }
      
      // If it's an addon, check the limits
      if (this.moduleData.subscription_details?.category === 'addon') {
        const { quantity, used_quantity } = this.moduleData.subscription_details;
        console.log('used_quantity', used_quantity);
        console.log('quantity', quantity);        
        if (used_quantity >= quantity) {
          console.log('Addon limit reached or exceeded');
          this.showAddonLimitReached = true;
          this.addonLimitMessage = `${this.titleCase(this.currentModuleName)} Add-on limit reached (${quantity}). Add more.`;
          return true;
        }
      }
      
      return false;
    },
    
    handleAddonLimits() {
      // This is now just a wrapper for backward compatibility
      return !this.shouldShowOverlay();
    },  
    
    gotToCheckout() {
      window.location.href = '/billing';
    },
    
    async payOutstandingInvoice() {
      try {
        // Show loading state
        this.isLoading = true;
    
        // Get the subscription ID from subscription details
        const subscriptionId = this.moduleData?.subscription_details?.stripe_subscription_id;
        
        // Fetch the latest invoice for this subscription
        const response = await axios.get(`/billing/subscription/${subscriptionId}/latest-invoice`);
        console.log('Invoice API response:', response.data);
        
        // Check for hosted invoice URL first
        if (response.data.hosted_invoice_url) {
          window.open(response.data.hosted_invoice_url, '_blank');
        } 
        // Fallback to billing portal
        else {
          console.warn('No payment URL found, falling back to billing portal');
          window.location.href = '/billing/portal';
        }
      } catch (error) {
        console.error('Error getting payment URL:', error);
        // On error, redirect to billing portal as fallback
        window.location.href = '/billing/portal';
      } finally {
        this.isLoading = false;
      }
    },

    async addAddon(plan) {
      console.log('plan', plan);
      this.isLoading = true;
      this.error = null;

      try {
        const priceId = plan.stripe_price_id || plan.id;
        const isUpdate = plan.isUpdate || this.showAddonLimitReached || false;
        const subscriptionId = plan.subscriptionId || this.moduleData?.subscription_details?.stripe_subscription_id || null;
        let quantity = plan.quantity || 1;

        // Calculate the final quantity to use
        if (isUpdate) {
          // When updating, use totalQuantity which should be existing + new
          quantity = this.getTotalAddonQuantity(plan);
        }
        
        const payload = {
          price_id: priceId,
          quantity: quantity,
          update: isUpdate,
          ...(subscriptionId ? { subscription_id: String(subscriptionId) } : {})
        };
        
        const response = await axios.post('/subscription/addon', payload);

        if (response.data.redirect_url) {
          // Open the Stripe hosted invoice in a new tab when updating
          if (isUpdate) {
            window.open(response.data.redirect_url, '_blank', 'noopener,noreferrer');
            return;
          }
          // Open the Stripe hosted checkout session in the same tab when adding
          window.location.href = response.data.redirect_url;
          return;
        }

        if (response.data.success) {
          this.$emit('addon-added', response.data.subscription);
          this.$store.dispatch('moduleAccess/fetchModulesAccessData');
          this.$notify({
            type: 'success',
            title: 'Success',
            text: response.data.message
          });
          this.close();
        } else {
          this.$notify({
            type: 'error',
            title: 'Error',
            text: response.data.error || 'Failed to add addon'
          });
          throw new Error(response.data.error || 'Failed to add addon');
        }

        if (!priceId) {
          throw new Error('No price ID found for this plan');
        }
      } catch (error) {
        console.error('Upgrade failed:', error);
        this.error = error.response?.data?.error || 'Failed to initiate upgrade. Please try again.';
      } finally {
        this.isLoading = false;
      }
    },

    getBlockSize(addon) {
      const val = addon?.block_size;
      const parsed = parseInt(val, 10);
      return isNaN(parsed) ? 5 : parsed;
    },

    getTotalAddonQuantity(addon) {
      const existingQuantity = parseInt(this.moduleData?.subscription_details?.quantity || 0, 10);
      const inputQuantity = parseInt(this.addonQuantities[addon.id] || addon?.block_size || 0, 10);
      const total = existingQuantity + inputQuantity;
      return isNaN(total) ? 5 : total;
    },
    updateAddonQuantity(event, addon) {
      const value = parseInt(event.target.value, 10);
      const blockSize = this.getBlockSize(addon);

      if (isNaN(value) || value < blockSize) {
        this.$set(this.addonQuantities, addon.id, blockSize);
        return;
      }

      const roundedValue = Math.max(blockSize, Math.floor(value / blockSize) * blockSize);
      this.$set(this.addonQuantities, addon.id, roundedValue);
    },

    totalAmount(addon) {
      const quantity = this.addonQuantities[addon.id] || this.getBlockSize(addon);
      const pricePerUser = addon.amount;
      return pricePerUser * quantity;
    },
    title(plan) {
      if (plan?.category === 'plan')  
        return `Upgrade Required for "${this.titleCase(this.currentModuleName)}"`;
      return `Add-on Required for "${this.titleCase(this.currentModuleName)}"`;
    },
    description(plan) {
      if (plan?.category === 'plan')
        return `To access the features of the "${this.titleCase(this.currentModuleName)}" module, you need to upgrade your plan.`;
      return `To access the features of the "${this.titleCase(this.currentModuleName)}" module, you need to purchase the corresponding add-on.`;
    },
    getFeatureName(feature) {
      return typeof feature === "string"
        ? feature
        : feature.name || feature.id || "";
    },
    titleCase(str) {
      return str?.replace(/\b\w/g, (match) => match.toUpperCase());
    },
    getFeatureKey(feature) {
      return typeof feature === "string"
        ? feature
        : feature.id || feature.name || JSON.stringify(feature);
    }
  }
}
</script>

<style scoped>
.modal-container {
  position: relative;
  z-index: 50;
}

.v--modal-overlay {
  position: absolute !important;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 2rem;
  z-index: 1200;
}

.v--modal-box {
  position: relative !important;
  margin: 0 auto;
  overflow-y: auto;
  max-height: 90vh;
  top: 0 !important;
  left: 0 !important;
  transform: none !important;
  margin-top: 2rem;
}
</style>
